import { ChevronRight } from "lucide-react"
import { useState } from "react"

import { Badge } from "@/components/ui/badge"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import { cn } from "@/lib/utils"

export type SendStatus = "queued" | "sent" | "failed" | "skipped"

export interface SendHistoryEntry {
  id: number
  delivery_kind: "manual" | "scheduled"
  view_name: string | null
  recipient_email: string | null
  formats: string[]
  subject: string
  status: SendStatus
  skip_reason_label: string | null
  sent_at: string
  delivered_at: string | null
  failed_at: string | null
  sent_by_name: string | null
  sent_by_email: string | null
}

export function sendHistoryDeliveryLabel(entry: SendHistoryEntry): string {
  return entry.delivery_kind === "scheduled"
    ? `Scheduled · ${entry.view_name || "Default"}`
    : "Manual"
}

interface Props {
  entries: SendHistoryEntry[]
  emptyLabel?: string
}

function SendStatusBadge({ status }: { status: SendStatus }) {
  switch (status) {
    case "sent":
      return (
        <Badge className="border-transparent bg-emerald-600 text-white">
          Sent
        </Badge>
      )
    case "failed":
      return <Badge variant="destructive">Failed</Badge>
    case "skipped":
      return <Badge variant="outline">Skipped</Badge>
    default:
      return <Badge variant="secondary">Queued</Badge>
  }
}

/** Compact report-send audit trail shared by invoicing and report pages. */
export default function SendHistorySection({
  entries,
  emptyLabel = "Not sent yet.",
}: Props) {
  const [open, setOpen] = useState(false)

  if (entries.length === 0) {
    return <p className="text-muted-foreground text-xs">{emptyLabel}</p>
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="text-muted-foreground hover:text-foreground inline-flex min-h-11 items-center gap-1.5 py-2 text-xs"
        >
          <ChevronRight
            className={cn(
              "h-3.5 w-3.5 transition-transform",
              open && "rotate-90",
            )}
          />
          Send history ({entries.length})
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-2">
        <p className="text-muted-foreground mb-1.5 text-[11px] leading-snug">
          “Sent” means the mail server accepted the message — not a confirmed
          inbox delivery.
        </p>
        <ul className="divide-border divide-y text-xs">
          {entries.map((entry) => {
            const timestamp =
              entry.status === "sent"
                ? (entry.delivered_at ?? entry.sent_at)
                : entry.status === "failed"
                  ? (entry.failed_at ?? entry.sent_at)
                  : entry.sent_at
            const timestampTitle =
              entry.status === "sent"
                ? "Accepted by the mail server"
                : entry.status === "failed"
                  ? "Retry attempts exhausted"
                  : entry.status === "skipped"
                    ? `Skipped — ${entry.skip_reason_label ?? "nothing to send"}`
                    : "Queued"

            return (
              <li
                key={entry.id}
                className="flex items-center justify-between gap-3 py-1.5"
              >
                <span className="flex min-w-0 items-center gap-2">
                  <SendStatusBadge status={entry.status} />
                  <span className="min-w-0">
                    <span className="block truncate">
                      <span className="text-foreground font-medium">
                        {entry.recipient_email || "Recipient unavailable"}
                      </span>
                      <span className="text-muted-foreground">
                        {" · "}
                        {sendHistoryDeliveryLabel(entry)} ·{" "}
                        {entry.formats
                          .map((format) => format.toUpperCase())
                          .join(" + ")}
                        {entry.sent_by_name && <> · {entry.sent_by_name}</>}
                      </span>
                    </span>
                    {entry.status === "skipped" && entry.skip_reason_label && (
                      <span
                        className="text-muted-foreground block truncate"
                        title={entry.skip_reason_label}
                      >
                        {entry.skip_reason_label}
                      </span>
                    )}
                  </span>
                </span>
                <span
                  className="text-muted-foreground shrink-0 tabular-nums"
                  title={timestampTitle}
                >
                  {new Date(timestamp).toLocaleString()}
                </span>
              </li>
            )
          })}
        </ul>
      </CollapsibleContent>
    </Collapsible>
  )
}
